/**
 * RequestAccessForm — public form for prospective users to request access.
 *
 * Flow:
 *   1. User fills in name, email, company, message
 *   2. POST /auth/request-access
 *   3. If status === 'approved' (already approved / has code), show "You're in" message
 *      If status === 'pending', show ticket ID + check-status link
 *      If status === 'rejected', show "contact admin" message
 */
import React, { useState } from 'react';
import { AuthService } from '../services/authService';

interface RequestAccessFormProps {
  onSwitchToRedeem: () => void;
  onSwitchToLogin: () => void;
  onBack: () => void;
}

export default function RequestAccessForm({ onSwitchToRedeem, onSwitchToLogin, onBack }: RequestAccessFormProps) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [ticketId, setTicketId] = useState<string | null>(null);
  const [result, setResult] = useState<{ status: string; message: string } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !name.trim()) {
      setError('Vul je naam en e-mail in.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const r = await AuthService.requestAccess({
        email: email.trim(),
        name: name.trim(),
        company: company.trim() || undefined,
        message: message.trim() || undefined,
      });
      setResult({ status: r.status, message: r.message });
      if (r.ticketId) setTicketId(r.ticketId);
    } catch (err: any) {
      setError(err?.message || 'Aanvraag mislukt');
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <div className="space-y-5">
        <div className={`rounded-2xl border p-5 ${
          result.status === 'approved' ? 'border-emerald-500/30 bg-emerald-500/10' :
          result.status === 'rejected' ? 'border-rose-500/30 bg-rose-500/10' :
          'border-amber-500/30 bg-amber-500/10'
        }`}>
          <p className="text-[10px] font-black uppercase tracking-[0.3em] opacity-70">
            {result.status === 'approved' ? 'Toegang verleend' :
             result.status === 'rejected' ? 'Afgewezen' :
             'Aanvraag ontvangen'}
          </p>
          <p className="mt-3 text-base text-white leading-relaxed">{result.message}</p>
        </div>

        {ticketId && (
          <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400">Ticket ID</p>
            <p className="mt-2 font-mono text-xs text-slate-200 break-all">{ticketId}</p>
            <p className="mt-3 text-xs text-slate-400">
              Bewaar dit ticket ID. Je kunt de status opvragen via{' '}
              <code className="text-indigo-300">/status?ticket={ticketId.slice(0, 8)}</code>.
            </p>
          </div>
        )}

        {result.status === 'approved' && (
          <button
            type="button"
            onClick={onSwitchToLogin}
            className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-bold uppercase tracking-[0.18em] text-white transition hover:border-white/25 hover:bg-white/10"
          >
            Naar login →
          </button>
        )}

        <button type="button" onClick={onBack} className="w-full text-xs text-slate-500 hover:text-slate-300">
          ← Terug
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400">Naam *</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          className="mt-2 w-full rounded-2xl border border-white/10 bg-[#0b1120] px-4 py-3 text-sm text-white placeholder-slate-500 outline-none focus:border-indigo-400"
          placeholder="Jan de Vries"
        />
      </div>
      <div>
        <label className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400">E-mail *</label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className="mt-2 w-full rounded-2xl border border-white/10 bg-[#0b1120] px-4 py-3 text-sm text-white placeholder-slate-500 outline-none focus:border-indigo-400"
          placeholder="jan@bedrijf.nl"
        />
      </div>
      <div>
        <label className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400">Bedrijf</label>
        <input
          type="text"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          className="mt-2 w-full rounded-2xl border border-white/10 bg-[#0b1120] px-4 py-3 text-sm text-white placeholder-slate-500 outline-none focus:border-indigo-400"
          placeholder="Voorbeeld BV (optioneel)"
        />
      </div>
      <div>
        <label className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400">Bericht</label>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={3}
          className="mt-2 w-full rounded-2xl border border-white/10 bg-[#0b1120] px-4 py-3 text-sm text-white placeholder-slate-500 outline-none focus:border-indigo-400"
          placeholder="Waarom wil je BDAT testen? (optioneel)"
        />
      </div>

      {error && (
        <div className="rounded-2xl border border-rose-500/30 bg-rose-500/10 p-3 text-sm text-rose-200">{error}</div>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="w-full rounded-2xl bg-gradient-to-r from-indigo-500 to-violet-500 px-4 py-3 text-sm font-black uppercase tracking-[0.18em] text-white transition hover:from-indigo-400 hover:to-violet-400 disabled:opacity-50"
      >
        {submitting ? 'Versturen…' : 'Toegang aanvragen'}
      </button>

      <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-[0.25em]">
        <button type="button" onClick={onSwitchToRedeem} className="text-slate-500 hover:text-slate-300">
          Heb je een code?
        </button>
        <button type="button" onClick={onSwitchToLogin} className="text-indigo-300 hover:text-indigo-200">
          Inloggen →
        </button>
      </div>
    </form>
  );
}
